import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';

import {endOfMonth, isSameMonth, isSameYear, startOfMonth} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import debounce from 'lodash/debounce';
import {useTranslation} from 'react-i18next';
import {generatePath, useParams} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {dateModel} from '@entities/date/model';
import {TaxpayerContractorsGate, taxpayerDetailsModel} from '@entities/taxpayers/detail-model';
import {GridFieldConfig} from '@shared/types/grid';
import {Input} from '@shared/ui/Input';
import {Link} from '@shared/ui/Link';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {mapTableColumns} from '@shared/ui/Table2.0';

import {ActionGroup, ContractorsTable, Controls, FilterButton, SearchBox} from '../styled';

import {formatAmount, riskColor, Status, statusColor} from './helpers';

const config = (t: any): GridFieldConfig[] => [
    {id: 'tin', header: t('page_taxpayer.contractors.tin'), width: 160},
    {id: 'name', header: t('page_taxpayer.contractors.name'), minWidth: 260},
    {id: 'liquidated', header: t('page_taxpayer.contractors.status'), width: 160},
    {id: 'surColour', header: t('page_taxpayer.contractors.sur'), width: 140, isSortable: true},
    {id: 'transitOrGap', header: t('page_taxpayer.contractors.type'), minWidth: 180},
    {id: 'decType', header: t('page_taxpayer.contractors.dec_type'), minWidth: 180},
    {id: 'quantity', header: t('page_taxpayer.contractors.quantity'), width: 150},
    {
        id: 'priceItems',
        header: t('page_taxpayer.contractors.price_items'),
        minWidth: 200,
        isSortable: true,
    },
    {
        id: 'taxAmount',
        header: t('page_taxpayer.contractors.tax_amount'),
        minWidth: 200,
        isSortable: true,
    },
    {
        id: 'shareDeduction',
        header: t('page_taxpayer.contractors.share_deduction'),
        width: 180,
        isSortable: true,
    },
];

const decTypeLabel = (value?: string | null) => {
    if (value === 'NOT_FILED') return 'terms.declaration.type.not_filled';
    if (value === 'PAYABLE') return 'terms.declaration.type.payable';
    if (value === 'DEDUCTION') return 'terms.declaration.type.deduction';
    if (value === 'ZERO') return 'terms.declaration.type.zero';
    return '';
};

const contractorViews = ['seller', 'buyer'] as const;

const taxpayerLink = (tin: string) => ({
    pathname: generatePath(ROUTES.TAXPAYER_DETAILS, {tin}),
    state: {from: location.pathname},
});

const SummaryValue = ({children}: {children: React.ReactNode}) => (
    <strong style={{fontSize: 14}}>{children}</strong>
);

const hasSelectedMonth = (from: Date | null, to: Date | null) =>
    Boolean(from && to && isSameYear(from, to) && isSameMonth(from, to));

export const ContractorsTab = () => {
    const {tin} = useParams<{tin: string}>();
    const {t} = useTranslation();
    const taxpayer = useUnit(taxpayerDetailsModel);
    const date = useUnit(dateModel);
    const [search, setSearch] = useState(taxpayer.state.contractorSearch);
    useGate(TaxpayerContractorsGate, {tin});

    useEffect(() => {
        const {from, to} = date.state.range;
        if (hasSelectedMonth(from, to)) return;

        const today = new Date();
        date.changed([startOfMonth(today), endOfMonth(today)]);
    }, [date]);

    const debounced = useMemo(
        () => debounce((value: string) => taxpayer.contractorSearchChanged(value), 700),
        [],
    );
    useEffect(() => {
        if (search === taxpayer.state.contractorSearch) return undefined;

        debounced(search);
        return debounced.cancel;
    }, [search, taxpayer.state.contractorSearch, debounced]);

    const contractors = taxpayer.state.contractors;
    const data = (contractors?.contractors ?? []).map((row: any) => ({
        ...row,
        tin: <Link to={taxpayerLink(row.tin)}>{row.tin}</Link>,
        name: <Link to={taxpayerLink(row.tin)}>{row.name}</Link>,
        liquidated: (
            <Status color={statusColor(row.liquidated)}>
                {t(row.liquidated ? 'terms.taxpayer.liquidated' : 'terms.taxpayer.not_liquidated')}
            </Status>
        ),
        surColour: (
            <StatusWrapper color={riskColor(row.surColour)}>{row.surColour ?? '—'}</StatusWrapper>
        ),
        transitOrGap: row.transitOrGap
            ? t(`terms.taxpayer.${row.transitOrGap.toLowerCase()}`)
            : '—',
        decType: row.decType && decTypeLabel(row.decType) ? t(decTypeLabel(row.decType)) : '—',
        quantity: formatAmount(row.quantity),
        priceItems: formatAmount(row.priceItems),
        taxAmount: formatAmount(row.taxAmount),
        shareDeduction: row.shareDeduction ? `${Math.round(Number(row.shareDeduction))}%` : '—',
    }));

    if (contractors) {
        data.unshift({
            tin: <SummaryValue>{t('components.table.Total').toUpperCase()}</SummaryValue>,
            name: '',
            liquidated: '',
            surColour: '',
            transitOrGap: '',
            decType: '',
            quantity: '',
            priceItems: <SummaryValue>{formatAmount(contractors.totalPriceItems)}</SummaryValue>,
            taxAmount: <SummaryValue>{formatAmount(contractors.totalTaxAmount)}</SummaryValue>,
            shareDeduction: '',
            isSummary: true,
        });
    }

    return (
        <>
            <Controls>
                <ActionGroup>
                    {contractorViews.map((view) => (
                        <FilterButton
                            key={view}
                            type="button"
                            isActive={taxpayer.state.contractorView === view}
                            onClick={() => taxpayer.contractorViewChanged(view)}
                        >
                            {t(`page_taxpayer.contractors.${view}`)}
                        </FilterButton>
                    ))}
                </ActionGroup>
                <SearchBox>
                    <Input
                        value={search}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                        placeholder={t('page_taxpayer.contractors.search')}
                    />
                </SearchBox>
            </Controls>
            <ContractorsTable
                columns={mapTableColumns(
                    config(t),
                    undefined,
                    undefined,
                    undefined,
                    taxpayer.state.contractorSortings,
                    (items) =>
                        taxpayer.contractorSortingsChanged(
                            items.reduce((acc, item) => ({...acc, [item.id]: item.value}), {}),
                        ),
                )}
                data={data}
                isLoading={taxpayer.state.pending}
                pageCount={taxpayer.state.contractors?.totalPages ?? 0}
                pageIndex={taxpayer.state.contractorPage}
                totalRowsCount={taxpayer.state.contractors?.totalItems ?? 0}
                onPageChange={taxpayer.contractorPageChanged}
                onPageSizeChange={taxpayer.contractorPageSizeChanged}
            />
        </>
    );
};
